import { useMergeRefs } from "@floating-ui/react";
import { ProductCategoryOverlay } from "./overlays/ProductCategoryOverlay";
import { productCategoryOverlays } from "./overlays/productCategoryOverlays.shared";
import { MessagesNavigationOverlay } from "./overlays/MessagesNavigationOverlay";
import { SellarCreditNavigationOverlay } from "./overlays/SellarCreditNavigationOverlay";
import { Button, Icon, zFloating, type IconType } from "@sellar-io/components";
import { Link, NavLink, useNavigate } from "react-router";
import { useIntercom } from "react-use-intercom";
import { NotificationsNavigationOverlay } from "./overlays/NotificationsNavigationOverlay";
import { useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import * as ROUTES from "../../constants/routes";
import { DiscoveryMarketSearch } from "../discovery/components/DiscoveryMarketSearch";
import { useDiscoveryV2SearchStore } from "../discovery/v2/DiscoveryV2SearchStore";
import { createScrollRestorationState } from "../../utils/scrollRestoration";
import sellarLogo from "./assets/sellar-logo.png";
import { RetailerCart } from "./RetailerCart";
import { retailerContentClasses } from "./retailerLayoutClasses";
import { AccountNavigationOverlay } from "./overlays/AccountNavigationOverlay";
import { BusinessNavigationOverlay } from "./overlays/BusinessNavigationOverlay";

const focusClasses =
  "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-purple-600";
const controlClasses =
  "relative inline-flex size-8 shrink-0 items-center justify-center rounded-full p-0 text-zinc-800 hover:text-[#a34fd2]";
const navigationClasses =
  "inline-flex min-h-5 min-[641px]:min-h-11 shrink-0 items-center gap-1 whitespace-nowrap text-sm font-medium text-zinc-800 hover:text-[#a34fd2]";

const NavigationIcon = ({
  label,
  icon,
  to,
  className,
  onClick,
  unread = false,
}: {
  label: string;
  icon: IconType;
  to?: string;
  className?: string;
  onClick?: () => void;
  unread?: boolean;
}) => {
  const classes = twMerge(controlClasses, focusClasses, className);
  const content = (
    <>
      <Icon icon={icon} size="20px" />
      {unread && (
        <>
          <span
            aria-hidden="true"
            className="absolute top-0.5 right-0.5 size-1.5 rounded-full bg-[#a34fd2]"
          />
          <span className="sr-only">Unread activity</span>
        </>
      )}
    </>
  );

  if (to) {
    return (
      <NavLink
        to={to}
        aria-label={unread ? `${label}, unread activity` : label}
        className={({ isActive }) =>
          twMerge(classes, isActive && "bg-purple-50 text-[#a34fd2]")
        }
      >
        {content}
      </NavLink>
    );
  }

  return (
    <Button
      color="transparent"
      aria-label={label}
      onClick={onClick}
      className={twMerge(classes, "hover:bg-transparent")}
    >
      {content}
    </Button>
  );
};

export const RetailerNavigation = () => {
  const intercom = useIntercom();
  const navigate = useNavigate();
  const [searchPortalRoot, setSearchPortalRoot] =
    useState<HTMLDivElement | null>(null);

  const navigationRef = useRef<HTMLDivElement>(null);
  const setNavigationRef = useMergeRefs([navigationRef, setSearchPortalRoot]);

  const submitSearch = (value: string) => {
    const search = value.trim();
    if (!search) {
      return;
    }
    useDiscoveryV2SearchStore.getState().setSearchValue(search);
    navigate(ROUTES.DISCOVER_SEARCH, {
      state: createScrollRestorationState(),
    });
  };

  return (
    <div ref={setNavigationRef}>
      <div
        className={twMerge(
          retailerContentClasses,
          "grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2 min-[641px]:grid-cols-[auto_minmax(0,1fr)_auto] min-[641px]:gap-x-6 min-[641px]:py-3",
        )}
      >
        <Link
          to={ROUTES.RETAILER_HOME}
          aria-label="Sellar home"
          className={twMerge(
            "row-start-1 inline-flex h-14 items-center rounded-sm min-[641px]:h-auto",
            focusClasses,
          )}
        >
          <img
            src={sellarLogo}
            alt="Sellar"
            width={1351}
            height={352}
            className="h-5 w-auto min-[641px]:h-8"
          />
        </Link>
        <div className="col-span-2 row-start-2 min-w-0 min-[641px]:col-span-1 min-[641px]:col-start-2 min-[641px]:row-start-1 min-[641px]:mt-0">
          <DiscoveryMarketSearch
            className="w-full min-[641px]:max-w-[420px]"
            fullWidth
            label="Search products, suppliers and styles"
            placeholder="Search products, suppliers and styles"
            inputContainerClassName="h-[46px] rounded-full border-zinc-200 bg-zinc-100 focus-within:border-[#a34fd2] focus-within:bg-white focus-within:shadow-[2px_2px_16px_0_rgba(0,0,0,0.1),0_0_2px_2px_rgba(163,79,210,0.2)] min-[641px]:h-[38px]"
            fallbackSubmitButtonClassName="m-0 size-6 rounded-none border-0 bg-transparent p-0"
            inputClassName="min-w-0 text-base placeholder:text-zinc-500 min-[641px]:text-sm"
            panelClassName={zFloating.navigationBar.popover}
            portalRoot={searchPortalRoot}
            onSubmit={submitSearch}
          />
        </div>
        <div className="col-start-2 row-start-1 flex items-center gap-2 justify-self-end min-[641px]:col-start-3 min-[641px]:gap-6">
          <NavigationIcon
            label="Help"
            icon="HELP_CIRCLE_OUTLINE"
            className="hidden lg:inline-flex"
            onClick={() => intercom.show()}
          />
          <MessagesNavigationOverlay />
          <NotificationsNavigationOverlay />
          <div aria-hidden="true" className="h-6 border-l border-zinc-200" />
          <BusinessNavigationOverlay />
          <AccountNavigationOverlay
            className={twMerge(controlClasses, focusClasses)}
          />
          <RetailerCart
            className={twMerge(
              controlClasses,
              focusClasses,
              "relative bg-zinc-100 hover:bg-zinc-100",
            )}
          />
        </div>
      </div>
      <nav
        aria-label="Marketplace"
        className="border-zinc-200 min-[641px]:border-t"
      >
        <div
          className={twMerge(
            retailerContentClasses,
            "[scrollbar-width:none] overflow-x-auto py-4 max-[640px]:px-5 min-[641px]:py-0 [&::-webkit-scrollbar]:hidden",
          )}
        >
          <div className="flex w-full min-w-max items-center min-[641px]:gap-6">
            <div className="flex shrink-0 items-center min-[641px]:gap-6">
              {productCategoryOverlays.map((config) => (
                <ProductCategoryOverlay
                  key={config.categoryKey}
                  config={config}
                  navigationRef={navigationRef}
                  trigger={(props) => (
                    <button
                      {...props}
                      type="button"
                      className={twMerge(
                        navigationClasses,
                        focusClasses,
                        "border-l border-zinc-200 px-3 first:border-l-0 first:pl-0 hover:text-[#8c23c7] aria-expanded:font-semibold aria-expanded:text-[#8c23c7] min-[641px]:gap-1.5 min-[641px]:border-l-0 min-[641px]:px-0",
                      )}
                    >
                      {config.label}
                      <Icon
                        icon="CHEVRON_DOWN"
                        size="12px"
                        className="hidden min-[641px]:inline-flex"
                      />
                    </button>
                  )}
                />
              ))}
              <button
                type="button"
                className={twMerge(
                  navigationClasses,
                  focusClasses,
                  "hidden min-[641px]:inline-flex",
                )}
              >
                More
              </button>
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-6">
              <Link
                to={ROUTES.SUPPLIERS}
                className={twMerge(
                  navigationClasses,
                  focusClasses,
                  "border-l border-zinc-200 pl-3 min-[641px]:border-l-0 min-[641px]:pl-0",
                )}
              >
                My Suppliers
              </Link>
              <span
                aria-hidden="true"
                className="hidden h-4 border-l border-zinc-200 min-[641px]:block"
              />
              <Link
                to={ROUTES.ORDERS}
                className={twMerge(
                  navigationClasses,
                  focusClasses,
                  "hidden min-[641px]:inline-flex",
                )}
              >
                Orders
              </Link>
              <span
                aria-hidden="true"
                className="hidden h-4 border-l border-zinc-200 min-[641px]:block"
              />
              <div className="hidden min-[641px]:block">
                <SellarCreditNavigationOverlay />
              </div>
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
};
